/**
 * AudioSystem — the facade main.ts talks to. Owns the context, engine,
 * director and music machine; maps options (09 §4) onto buses; unlocks on
 * the first gesture (08 §9). Everything below it is DOM-free.
 */
import type { Arena, MatchPhase, Team } from '../../../sim/types.js'
import type { SimEvent } from '../../../shared/events.js'
import type { Options } from '../options.js'
import type { AudioContextLike } from './context.js'
import { AudioEngine, type EngineDebug, type FilesReport } from './engine.js'
import { SOUND_FILES } from './files.js'
import type { CameraParams } from '../camera.js'
import { AudioDirector, fireTriggers, mapSimEvent, type EventContext, type WorldFrame } from './events.js'
import { SOUNDS, SOUND_IDS, isAmbience, isStem, type SoundId } from './manifest.js'
import { MusicStateMachine, type MusicInfo, type MusicState } from './music.js'

export type { AudioContextLike } from './context.js'
export type { EventContext, WorldFrame, AudioTrigger } from './events.js'
export type { MusicState } from './music.js'
export type { SoundId } from './manifest.js'
export { mapSimEvent } from './events.js'
export { SOUNDS, SOUND_IDS } from './manifest.js'

export interface AudioSystemOptions { options: Options; arena: Arena; createContext?: () => AudioContextLike }

export interface AudioDebugInfo extends EngineDebug { music: ReturnType<MusicStateMachine['debug']> }

export class AudioSystem {
  readonly engine: AudioEngine
  readonly director: AudioDirector
  readonly music: MusicStateMachine
  private options: Options
  private unlockInstalled = false
  /** Result of the post-unlock stem download (08 §8 lazy stems); null until it settles. */
  filesReport: FilesReport | null = null

  constructor(opts: AudioSystemOptions) {
    const ctx = opts.createContext ? opts.createContext() : new (globalThis as unknown as { AudioContext: new () => AudioContextLike }).AudioContext()
    this.engine = new AudioEngine(ctx, { arena: opts.arena })
    this.director = new AudioDirector(this.engine)
    this.music = new MusicStateMachine(this.engine)
    this.options = opts.options
    this.applyOptions(opts.options)
  }

  get unlocked(): boolean { return this.engine.unlocked }

  /** Resume on the first pointerdown/keydown/gamepad press; earlier events are dropped. */
  /** Spec §5 SOUND BANK loading step. Stems and ambience are long; they render lazily as before. */
  prewarm(onProgress: (done01: number) => void): Promise<void> {
    const ids = SOUND_IDS.filter((id) => !isStem(id) && !isAmbience(id))
    return this.engine.prewarm(ids, onProgress)
  }

  installUnlock(targets: EventTarget[]): void {
    if (this.unlockInstalled) return
    this.unlockInstalled = true
    const once = () => { void this.unlock(); for (const t of targets) for (const ev of ['pointerdown', 'keydown']) t.removeEventListener(ev, once) }
    for (const t of targets) for (const ev of ['pointerdown', 'keydown']) t.addEventListener(ev, once)
    if (typeof window !== 'undefined') window.addEventListener('gamepadconnected', once, { once: true })
  }

  async unlock(): Promise<void> {
    if (this.engine.unlocked) return
    await this.engine.unlock()
    // 08 §8: stems are lazy — fetched in the background after the gesture; synth carries them until then.
    void this.engine.preloadFiles(SOUND_FILES).then((r) => { this.filesReport = r })
    // Re-enter the current music state so ambience/stems start from snapshot state.
    this.music.setState(this.music.state, { roundIndex: this.music.roundIndex })
  }

  applyOptions(o: Options): void {
    this.options = o
    const e = this.engine
    e.setBusOption('master', o['audio.master'])
    e.setBusOption('sfx', o['audio.sfx'])
    e.setBusOption('music', o['audio.music'])
    e.setBusOption('ui', o['audio.ui'])
    e.setBusOption('voice', o['audio.voice'])
    e.muteOnBlur = o['audio.muteOnBlur']
    if (e.reduced !== o['audio.reduced']) e.setReduced(o['audio.reduced'])
    if (e.mono !== o['audio.mono']) e.setMono(o['audio.mono'])
    this.music.setOptions({ hubMusic: o['audio.hubMusic'], reduced: o['audio.reduced'] })
  }

  /** Per-frame camera for spatialisation (spec §2). */
  setCamera(cam: CameraParams, arena: Arena): void { this.engine.setCamera(cam, arena) }
  setListener(team: Team): void { if (team !== this.engine.listenerTeam) { this.engine.setListenerTeam(team); this.engine.retargetAll() } }
  /** Spectator side switch: tick + re-pan every live voice (08 §4d). */
  setTeamSwitch(team: Team): void { this.engine.setListenerTeam(team); this.engine.retargetAll(); this.engine.play('ui_side_switch') }

  /** One snapshot's events. */
  ingest(events: SimEvent[], ctx: EventContext): void {
    if (!this.director.snapshot() || !this.engine.unlocked) return
    const hitInBatch = events.some((e) => e.type === 'hit')
    // S10: jump/land events are the primary trigger; the director's grounded-flip fallback shares the gate so neither doubles.
    const gated = events.filter((e) => !((e.type === 'jump' || e.type === 'land') && !this.director.jumpGate(e.playerId, e.type)))
    const triggers = gated.flatMap((e) => mapSimEvent(e, { ...ctx, hitInBatch }))
    const hasVoice = triggers.some((t) => SOUNDS[t.id].bus === 'voice')
    if (hasVoice) { this.engine.duckMusic(true); this.voiceUntil = this.engine.ctx.currentTime + 1.2 }
    fireTriggers(this.engine, triggers, this.music.nextBoundary(this.engine.ctx.currentTime))
  }
  private voiceUntil = 0

  frame(world: WorldFrame): void {
    this.director.frame(world)
    this.music.tick()
    if (this.voiceUntil && this.engine.ctx.currentTime >= this.voiceUntil) { this.voiceUntil = 0; this.engine.duckMusic(false) }
  }

  setMusic(state: MusicState, info: MusicInfo = {}): void { if (state !== this.music.state || info.roundIndex !== undefined && info.roundIndex !== this.music.roundIndex) this.music.setState(state, info) }

  /** Derive the music state from the room phase (helper for main.ts). */
  static musicFor(mode: 'hub' | 'match', phase: MatchPhase, timerS: number | null): MusicState {
    if (mode === 'hub') return 'HUB'
    switch (phase) {
      case 'LOBBY_PICK': return 'LOBBY_PICK'
      case 'LOBBY_LOCK': case 'REVEAL': return 'LOBBY_LOCK'
      case 'RESET': case 'COUNTDOWN': return 'COUNTDOWN'
      case 'LIVE': return timerS !== null && timerS <= 10 ? 'LIVE_LAST10' : 'LIVE'
      case 'OVERTIME': return 'OVERTIME'
      case 'ROUND_END': return 'ROUND_END'
      case 'BETWEEN': return 'BETWEEN'
      case 'RESULTS': case 'DONE': return 'RESULTS'
    }
  }

  /** UI sounds from HUD/options code. Slider previews play on their own bus (08 §7). */
  ui(id: SoundId): void { this.engine.play(id) }
  /** Spatialised one-shot from session code (kiosk headline, 08 §4). */
  at(id: SoundId, pos: { x: number; z: number }): void { this.engine.play(id, { pos }) }
  /** 08 §4: `pause_in` → faint `pause_loop` (−18 dB) → `pause_out`. */
  pause(on: boolean): void {
    if (on) { this.engine.play('pause_in'); this.engine.startLoop('ui:pause', 'pause_loop') }
    else { if (this.engine.hasLoop('ui:pause')) this.engine.stopLoop('ui:pause'); this.engine.play('pause_out') }
  }
  /** 08 §4 `stamina_refuse`: local player only — a dash press the sim will refuse (stamina below cost). */
  staminaRefuse(): void { this.engine.play('stamina_refuse') }

  onBlur(): void { this.engine.blur() }
  onFocus(): void { this.engine.focus() }
  hardResync(): void { this.director.hardResync(); this.music.stopAll(); this.music.setState(this.music.state, { roundIndex: this.music.roundIndex }) }

  debug(): AudioDebugInfo { return { ...this.engine.debug(), music: this.music.debug() } }
}

/** `?debug=audio` panel (14 R8): live voices, bus gains, music state. */
export function renderAudioDebug(el: HTMLElement, d: AudioDebugInfo): void {
  const lines = [
    `audio ${d.unlocked ? 'UNLOCKED' : 'locked'}${d.muted ? ' MUTED' : ''}${d.reduced ? ' reduced' : ''}${d.mono ? ' mono' : ''} listener ${d.listenerTeam === 0 ? 'A' : 'B'} files ${d.files}`,
    `buses master ${d.buses.master.toFixed(2)} sfx ${d.buses.sfx.toFixed(2)} music ${d.buses.music.toFixed(2)} ui ${d.buses.ui.toFixed(2)} voice ${d.buses.voice.toFixed(2)}`,
    `music ${d.music.state} r${d.music.roundIndex} stems [${d.music.stems.join(' ')}] amb [${d.music.ambience.join(' ')}]${d.music.pending ? ` → [${d.music.pending.stems.join(' ')}] @${d.music.pending.at.toFixed(2)}` : ''}`,
    `voices ${d.voices.length}`,
    ...d.voices.map((v) => `  ${v.id.padEnd(24)} p${v.pri} pan ${v.pan.toFixed(2).padStart(5)} g ${v.gain.toFixed(2)}${v.loop ? ' loop' : ''}${v.entity ? ` ${v.entity}` : ''}`),
  ]
  el.textContent = lines.join('\n')
}
